import { Link as MuiLink, TableCell, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "i18n-fe";
import { formatElapsedLabel } from "../util/scribePresence";

// Static keys only (CLAUDE.md: no dynamic i18n keys), one branch per exception.
function statusText(t, ex) {
  switch (ex.code) {
    case "stuck":
      return t("scribePresence.rowStatus.stuck", { time: formatElapsedLabel(ex.ageMs) });
    case "silent":
      return ex.unsent !== null && ex.unsent !== undefined && ex.unsent > 0
        ? t("scribePresence.rowStatus.silentUnsent", { time: formatElapsedLabel(ex.silentMs), count: ex.unsent })
        : t("scribePresence.rowStatus.silent", { time: formatElapsedLabel(ex.silentMs) });
    case "ringMovedOn":
      return t("scribePresence.rowStatus.ringMovedOn", { count: ex.count });
    case "needsAttention":
      return t("scribePresence.rowStatus.needsAttention", { count: ex.count });
    case "queued":
      return ex.ageMs == null
        ? t("scribePresence.rowStatus.queued", { count: ex.count })
        : t("scribePresence.rowStatus.queuedOldest", { count: ex.count, time: formatElapsedLabel(ex.ageMs) });
    case "oldBuild":
      return t("scribePresence.rowStatus.oldBuild");
    case "neverUploaded":
      return t("scribePresence.rowStatus.neverUploaded");
    default:
      return "";
  }
}

// #3647 -- the "Status" cell of a Scribe Station Status row: the ONE worst
// exception of the station sitting in that (ring, seat), as one short line in
// warning colour that never wraps (noWrap + ellipsis, the full text in the
// `title`). The whole cell is the link to the Station Monitor. No station
// exception means an empty cell.
//
// Same one-line contract as StationExceptionLine (the strip's element), but
// without its Alert chrome, its own link text and its margin, so the row keeps
// its height; the cell itself is the link target instead.
// On a phone the table scrolls sideways, so the last column would sit off
// screen. The Status column is therefore sticky to the right edge of the table's
// scroll box (header and cells alike, STATUS_STICKY), and keeps a short width on
// phones so the line truncates to an ellipsis instead of widening the row.
export const STATUS_STICKY = {
  position: "sticky",
  right: 0,
  bgcolor: "background.paper",
  borderLeft: 1,
  borderLeftColor: "divider",
};
const STATUS_WIDTH = { xs: 128, sm: 260 };

export default function StationRowStatus({ exception, testId }) {
  const { t } = useTranslation("scoreadmin");
  if (!exception) return <TableCell data-testid={testId} sx={{ ...STATUS_STICKY, width: STATUS_WIDTH, maxWidth: STATUS_WIDTH }} />;

  const text = statusText(t, exception);
  const color = exception.severity === "error" ? "error.dark" : "warning.dark";
  return (
    <TableCell data-testid={testId} sx={{ ...STATUS_STICKY, p: 0, width: STATUS_WIDTH, maxWidth: STATUS_WIDTH }}>
      <MuiLink
        component={RouterLink}
        to="/station-monitor"
        underline="hover"
        data-testid="station-exception-link"
        sx={{ display: "block", px: 2, py: 0.75, color }}
      >
        <Typography
          variant="body2"
          noWrap
          title={text}
          data-testid="station-exception-text"
          sx={{ color: "inherit", fontWeight: 500 }}
        >
          {text}
        </Typography>
      </MuiLink>
    </TableCell>
  );
}
